<template>
  <section id="how-it-works" class="tw-bg-white tw-py-24">
    <div class="tw-container tw-mx-auto tw-px-6">
      <div class="tw-text-center tw-mb-16">
        <h2 class="tw-text-4xl md:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
          How TopEshows Creates Deal-Generating Events
        </h2>
        <p class="tw-text-lg tw-text-gray-600 tw-max-w-2xl tw-mx-auto">
          Proven methodology used by organizers in GCC, MENA, LATAM
        </p>
      </div>

      <div class="tw-max-w-6xl tw-mx-auto">
        <div class="tw-relative">
          <!-- Flow Line with Arrows (hidden on mobile) -->
          <div class="tw-hidden lg:flex tw-absolute tw-top-8 tw-left-0 tw-right-0 tw-items-center tw-justify-between" style="margin: 0 2rem; padding: 0 1rem;">
            <div class="tw-flex-1 tw-h-0.5 tw-bg-primary-200"></div>
            <svg class="tw-w-5 tw-h-5 tw-text-primary-400 tw-mx-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <div class="tw-flex-1 tw-h-0.5 tw-bg-primary-200"></div>
            <svg class="tw-w-5 tw-h-5 tw-text-primary-400 tw-mx-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <div class="tw-flex-1 tw-h-0.5 tw-bg-primary-200"></div>
            <svg class="tw-w-5 tw-h-5 tw-text-primary-400 tw-mx-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <div class="tw-flex-1 tw-h-0.5 tw-bg-primary-200"></div>
          </div>
          
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-relative">
            <!-- Step 1 -->
            <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100 hover:tw-border-primary-200 tw-transition-colors">
              <div class="tw-w-14 tw-h-14 tw-bg-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-shadow-md tw-relative tw-z-10">
                <svg class="tw-w-7 tw-h-7 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">You Import</h3>
              <p class="tw-text-gray-600 tw-text-sm tw-leading-relaxed">Upload your attendee and exhibitor lists</p>
            </div>

            <!-- Step 2 -->
            <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100 hover:tw-border-primary-200 tw-transition-colors">
              <div class="tw-w-14 tw-h-14 tw-bg-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-shadow-md tw-relative tw-z-10">
                <svg class="tw-w-7 tw-h-7 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">We Match</h3>
              <p class="tw-text-gray-600 tw-text-sm tw-leading-relaxed">Algorithm creates optimal B2B matches</p>
            </div>

            <!-- Step 3 -->
            <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100 hover:tw-border-primary-200 tw-transition-colors">
              <div class="tw-w-14 tw-h-14 tw-bg-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-shadow-md tw-relative tw-z-10">
                <svg class="tw-w-7 tw-h-7 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">Attendees Meet</h3>
              <p class="tw-text-gray-600 tw-text-sm tw-leading-relaxed">Meetings scheduled pre-event</p>
            </div>

            <!-- Step 4 -->
            <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100 hover:tw-border-primary-200 tw-transition-colors">
              <div class="tw-w-14 tw-h-14 tw-bg-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-shadow-md tw-relative tw-z-10">
                <svg class="tw-w-7 tw-h-7 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">You Measure</h3>
              <p class="tw-text-gray-600 tw-text-sm tw-leading-relaxed">Post-event analytics dashboard</p>
            </div>
          </div>
        </div>

        <!-- CTA -->
        <div class="tw-text-center tw-mt-12 tw-hidden">
          <button class="tw-bg-primary-600 tw-text-white tw-px-8 tw-py-3 tw-rounded-lg tw-text-lg tw-font-semibold hover:tw-bg-primary-700 tw-transition-colors tw-shadow-md hover:tw-shadow-lg">
            See a Live Demo
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
